"use client"

import * as React from "react"
import { AlertTriangle, Loader2 } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { cn } from "@/lib/utils"

/**
 * Visual variant of the dialog.
 *  - "destructive": red accents, used for delete / leave / irreversible actions
 *  - "default":     neutral accents, used for benign confirmations
 */
export type ConfirmDialogVariant = "destructive" | "default"

export interface ConfirmDialogProps {
  /** Controls visibility. Parent owns this state. */
  open: boolean
  onOpenChange: (open: boolean) => void

  title: React.ReactNode
  description?: React.ReactNode

  /** Visible label for the confirm button. Defaults to "Confirm" / "Delete". */
  confirmLabel?: React.ReactNode
  /** Visible label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: React.ReactNode

  /**
   * Custom icon shown in the header. If omitted, a sensible default is used:
   *   - destructive variant → AlertTriangle
   *   - default variant     → no icon
   * Pass `null` to disable the icon entirely.
   */
  icon?: React.ReactNode | null

  variant?: ConfirmDialogVariant

  /**
   * Called when the user confirms. May return a promise; the dialog will show
   * a spinner on the confirm button until it resolves and will auto-close on
   * success. If the promise rejects, the dialog stays open so the user can
   * retry — callers should surface the error themselves (e.g. via a toast).
   */
  onConfirm: () => void | Promise<void>

  /**
   * Disable the confirm button (e.g. while a prerequisite is loading). The
   * cancel button is always enabled unless an in-flight confirm is running.
   */
  disabled?: boolean

  /** Optional extra className for the dialog content wrapper. */
  className?: string
}

const VARIANT_STYLES: Record<
  ConfirmDialogVariant,
  {
    border: string
    iconWrap: string
    iconColor: string
    confirmBtn: string
    defaultIcon: React.ReactNode
    defaultLabel: string
  }
> = {
  destructive: {
    border: "border-red-500/30",
    iconWrap: "bg-red-500/15",
    iconColor: "text-red-400",
    confirmBtn: "bg-red-600 hover:bg-red-500 focus-visible:ring-red-500 text-white",
    defaultIcon: <AlertTriangle className="h-5 w-5" />,
    defaultLabel: "Delete",
  },
  default: {
    border: "border-zinc-700",
    iconWrap: "bg-zinc-700/50",
    iconColor: "text-zinc-200",
    confirmBtn: "bg-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500 text-white",
    defaultIcon: null,
    defaultLabel: "Confirm",
  },
}

/**
 * A reusable confirmation dialog built on top of shadcn's <AlertDialog />.
 *
 * It's deliberately controlled so callers can drive open state from whatever
 * pattern fits — most commonly, a piece of state that holds the *target* of
 * the action (e.g. `pendingDeleteId`) and is set/cleared by the trigger and
 * the dialog's onOpenChange.
 *
 * @example
 *   <ConfirmDialog
 *     open={!!pendingDeleteId}
 *     onOpenChange={(o) => !o && setPendingDeleteId(null)}
 *     title="Delete this meme?"
 *     description="This action cannot be undone."
 *     confirmLabel="Delete"
 *     variant="destructive"
 *     onConfirm={async () => {
 *       await deleteMeme(pendingDeleteId!)
 *       setPendingDeleteId(null)
 *     }}
 *   />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  icon,
  variant = "destructive",
  onConfirm,
  disabled = false,
  className,
}: ConfirmDialogProps) {
  const styles = VARIANT_STYLES[variant]
  const resolvedIcon = icon === undefined ? styles.defaultIcon : icon
  const resolvedConfirmLabel = confirmLabel ?? styles.defaultLabel

  const [isPending, setIsPending] = React.useState(false)

  // Reset pending state whenever the dialog is closed externally so that a
  // re-open doesn't show a stale spinner.
  React.useEffect(() => {
    if (!open) setIsPending(false)
  }, [open])

  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the AlertDialog's default close-on-action behaviour; we close
    // only if onConfirm succeeds so failures keep the dialog open for retry.
    event.preventDefault()
    if (isPending || disabled) return
    try {
      setIsPending(true)
      await onConfirm()
      onOpenChange(false)
    } catch (err) {
      // Surfacing the error is the caller's job (typically a toast). We just
      // keep the dialog open and reset the spinner.
      console.error("ConfirmDialog: onConfirm threw", err)
    } finally {
      setIsPending(false)
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Block closing while a confirm is in flight to avoid orphaned spinners.
        if (isPending && !next) return
        onOpenChange(next)
      }}
    >
      <AlertDialogContent
        className={cn(
          "bg-zinc-950 text-white",
          styles.border,
          className
        )}
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            {resolvedIcon !== null && (
              <span
                className={cn(
                  "mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                  styles.iconWrap,
                  styles.iconColor
                )}
                aria-hidden="true"
              >
                {resolvedIcon}
              </span>
            )}
            <div className="flex-1 space-y-1.5">
              <AlertDialogTitle className="text-white">{title}</AlertDialogTitle>
              {description != null && (
                <AlertDialogDescription className="text-zinc-400">
                  {description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={isPending}
            className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 hover:text-white"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending || disabled}
            onClick={handleConfirm}
            className={cn("min-w-[7rem]", styles.confirmBtn)}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please wait…
              </>
            ) : (
              resolvedConfirmLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default ConfirmDialog
